import type { UserData } from "@/types/user-data";

/**
 * Extract a display-friendly first name from a user's profile data.
 *
 * Handles three shapes for `user_name`:
 *  - Full name with space ("Cliff Schenkhuizen") → "Cliff"
 *  - Dotted email prefix   ("cliff.schenkhuizen") → "Cliff"
 *  - Simple single name    ("Cliff")              → "Cliff"
 *
 * Falls back to the email local-part when `user_name` is empty:
 *  "cliff@schenkhuizen.com"          → "Cliff"
 *  "cliff.schenkhuizen@gmail.com"    → "Cliff"
 *
 * All results are trimmed and title-cased.
 */
export function extractFirstName(userData: UserData | null): string | null {
  if (!userData) return null;

  // AE-207: `first_name` is stored explicitly now, so prefer it over guessing from
  // `user_name`. It still needs normalizing: the backfill copies `user_name` verbatim, so a
  // row whose name was never edited holds the email local-part placeholder that
  // `userService.saveUserAcceptance` writes on first sign-in. Returning that raw would
  // reintroduce the exact bug HEY-151 reported ("Morning, cliff.schenkhuizen.").
  //
  // Dotted segments are split, spaces are NOT: a genuine multi-word first name
  // ("Ana Maria") is precisely what this column exists to preserve.
  const storedFirstName = userData.first_name?.trim();
  if (storedFirstName) {
    const firstSegment = storedFirstName.includes(".") ? storedFirstName.split(".")[0] : storedFirstName;
    return titleCase(firstSegment);
  }

  const name = userData.user_name?.trim();
  if (name) {
    // Full name with space — take first word
    if (name.includes(" ")) {
      return capitalize(name.split(" ")[0]);
    }
    // Dotted identifier — take first segment
    if (name.includes(".")) {
      return capitalize(name.split(".")[0]);
    }
    // Simple name — return as-is, capitalized
    return capitalize(name);
  }

  // Fallback: try email
  const email = userData.email?.trim();
  if (email) {
    const local = email.split("@")[0];
    // Even "cliff.schenkhuizen" → "Cliff"
    const first = local.includes(".") ? local.split(".")[0] : local;
    return capitalize(first);
  }

  return null;
}

function capitalize(word: string): string {
  if (!word) return word;
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

/**
 * Capitalize every word. Needed for the stored `first_name`, where `capitalize` alone would
 * turn "Ana Maria" into "Ana maria" by lowercasing everything after the first character.
 */
function titleCase(value: string): string {
  return value.split(/\s+/).filter(Boolean).map(capitalize).join(" ");
}
